import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ApiConfiguration } from '@klubovna/api-client';
import {
  workLogsControllerAdminCreate,
  WorkLogsControllerAdminCreate$Params,
  workLogsControllerApprove,
  workLogsControllerDelete,
  workLogsControllerFindAll,
  WorkLogsControllerFindAll$Params,
  workLogsControllerReject,
  WorkLogsControllerReject$Params,
  workLogsControllerUpdate,
  WorkLogsControllerUpdate$Params,
} from '@klubovna/api-client';
import {
  AdminCreateWorkLogDto,
  ApproveWorkLogDto,
  RejectWorkLogDto,
  UpdateWorkLogDto,
  WorkLog,
  WorkLogStatus,
} from '@klubovna/api-client';
import { PaginatedResponse, toPaginatedResponse } from '@klubovna/api-client';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface WorkLogsQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: WorkLogStatus;
  userId?: string;
  workTypeId?: string;
  sortBy?: 'createdAt' | 'updatedAt' | 'workDate' | 'hoursWorked' | 'earnedPoints' | 'workTypeName' | 'volunteerName';
  sortOrder?: 'ASC' | 'DESC';
}

@Injectable({
  providedIn: 'root',
})
export class WorkLogsApiService {
  private http = inject(HttpClient);
  private apiConfig = inject(ApiConfiguration);

  findAll(params?: WorkLogsQueryParams): Observable<PaginatedResponse<WorkLog>> {
    const apiParams: WorkLogsControllerFindAll$Params = {
      page: params?.page,
      limit: params?.limit,
      search: params?.search,
      status: params?.status,
      userId: params?.userId,
      workTypeId: params?.workTypeId,
      sortBy: params?.sortBy,
      sortOrder: params?.sortOrder,
    };

    return workLogsControllerFindAll(this.http, this.apiConfig.rootUrl, apiParams)
      .pipe(map(response => toPaginatedResponse<WorkLog>(response.body)));
  }

  adminCreate(workLogData: AdminCreateWorkLogDto): Observable<WorkLog> {
    const params: WorkLogsControllerAdminCreate$Params = {
      body: workLogData,
    };
    return workLogsControllerAdminCreate(this.http, this.apiConfig.rootUrl, params)
      .pipe(map(response => response.body as WorkLog));
  }

  approve(id: string, approveData?: ApproveWorkLogDto): Observable<WorkLog> {
    return workLogsControllerApprove(this.http, this.apiConfig.rootUrl, {
      id,
      body: approveData || {},
    })
      .pipe(map(response => response.body as WorkLog));
  }

  reject(id: string, rejectData: RejectWorkLogDto): Observable<WorkLog> {
    const params: WorkLogsControllerReject$Params = {
      id,
      body: rejectData,
    };
    return workLogsControllerReject(this.http, this.apiConfig.rootUrl, params)
      .pipe(map(response => response.body as WorkLog));
  }

  update(id: string, updateData: UpdateWorkLogDto): Observable<WorkLog> {
    const params: WorkLogsControllerUpdate$Params = {
      id,
      body: updateData,
    };
    return workLogsControllerUpdate(this.http, this.apiConfig.rootUrl, params)
      .pipe(map(response => response.body as WorkLog));
  }

  delete(id: string): Observable<void> {
    return workLogsControllerDelete(this.http, this.apiConfig.rootUrl, { id })
      .pipe(map(() => undefined));
  }
}
